import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

function fmtMonth(s) {
  if (!s) return s;
  try {
    const [y, m] = s.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("default", { month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("default", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const cols = Array.from(rows.reduce((acc, r) => { Object.keys(r).forEach((k) => acc.add(k)); return acc; }, new Set()));
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function ReportsLiveClosuresDrilldown() {
  const { month } = useParams();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("agent") || "";
  const navigate = useNavigate();
  const [data, setData] = useState({ leads: [], total: 0, agent_name: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ month });
        if (agentId) qs.set("agent_id", agentId);
        const { data } = await api.get(`/reports/live-closures-drilldown?${qs.toString()}`);
        setData(data || { leads: [], total: 0, agent_name: null });
      } finally {
        setLoading(false);
      }
    })();
  }, [month, agentId]);

  const workbenchUrl = (() => {
    const sp = new URLSearchParams();
    sp.set("live_month", month);
    if (agentId && agentId !== "__unassigned__") sp.set("lead_owner_id", agentId);
    return `/leads?${sp.toString()}`;
  })();

  const scopeLabel = agentId ? (data.agent_name || "Selected agent") : "All agents";

  return (
    <Layout
      title={`Live closures · ${fmtMonth(month)}`}
      breadcrumb={["Reports", "Live closures drilldown"]}
    >
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/reports")}
          className="text-sm font-medium text-slate-600 hover:text-orange-600 inline-flex items-center gap-1.5"
          data-testid="back-to-reports"
        >
          <ArrowLeft size={14} /> Back to Reports
        </button>
        <div className="flex items-center gap-2">
          <Link
            to={workbenchUrl}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:border-orange-400 hover:text-orange-600 inline-flex items-center gap-1.5"
            data-testid="open-in-workbench"
          >
            <ExternalLink size={14} /> Open in Workbench
          </Link>
          <button
            data-testid="download-live-drilldown"
            onClick={() => {
              const rows = (data.leads || []).map((l) => ({
                company: l.company_name,
                owner: l.lead_owner_name,
                current_stage: l.lead_stage,
                tag: l.stage_tagging,
                poc: l.client_poc_name,
                category: l.category,
                live_entered: l.live_entered_at,
                created: l.created_at,
              }));
              downloadCsv(`live-closures-${month}${agentId ? "-" + agentId : ""}`, rows);
            }}
            className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1.5"
          >
            <Download size={14} /> Download CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">
            Companies that entered Live · {fmtMonth(month)}
          </h3>
          <div className="text-xs text-slate-500">
            {loading ? "Loading…" : (
              <span data-testid="live-drilldown-total">
                <b>{data.total}</b> compan{data.total === 1 ? "y" : "ies"} · {scopeLabel}
              </span>
            )}
          </div>
        </div>

        <div className="text-xs text-slate-500 mb-3">
          Leads whose <b>first entry into the Live stage</b> was in {fmtMonth(month)}
          {agentId ? <> · owned by <b>{scopeLabel}</b></> : <> · across all agents</>}.
          Click any company to open its lead page.
        </div>

        {loading ? (
          <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
        ) : (data.leads || []).length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">No companies in this scope.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="live-drilldown-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 font-medium">
                  <th className="py-2 pr-4">Company</th>
                  {!agentId && <th className="py-2 px-3">Owner</th>}
                  <th className="py-2 px-3">Current stage</th>
                  <th className="py-2 px-3">Tag</th>
                  <th className="py-2 px-3">POC</th>
                  <th className="py-2 px-3">Category</th>
                  <th className="py-2 px-3 whitespace-nowrap">Live entered</th>
                </tr>
              </thead>
              <tbody>
                {(data.leads || []).map((l) => (
                  <tr
                    key={l.id}
                    className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer"
                    onClick={() => navigate(`/leads/${l.id}`)}
                    data-testid={`live-drilldown-row-${l.id}`}
                  >
                    <td className="py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">
                      <Link to={`/leads/${l.id}`} className="text-emerald-700 hover:text-emerald-900 hover:underline" onClick={(e) => e.stopPropagation()}>
                        {l.company_name || "—"}
                      </Link>
                    </td>
                    {!agentId && <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{l.lead_owner_name || "Unassigned"}</td>}
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{l.lead_stage || "—"}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{l.stage_tagging || "—"}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{l.client_poc_name || "—"}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{l.category || "—"}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap tabular-nums">{fmtDate(l.live_entered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
